import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import Header from "./Components/Header";
import { useNavigate } from "react-router-dom";
import PageContainer from "./Components/PageContainer";
import MainContent from "./Components/MainContent";
import SectionTitle from "./Components/SectionTitle";
import TutorCard from "./Components/TutorCard";
import {
  getTeacherDisplayName,
  getTeacherIdentifier,
  normalizeTeacherCollection,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  normalizeAudPriceForDisplay,
  normalizeAudPriceToMinorUnits,
} from "../../utils/audCurrency";

const buildAvatarDataUri = (name = "Teacher") => {
  const safeName = String(name || "Teacher").trim() || "Teacher";
  const initial = safeName.charAt(0).toUpperCase();
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#f97316" />
          <stop offset="100%" stop-color="#ef4444" />
        </linearGradient>
      </defs>
      <rect width="128" height="128" rx="24" fill="url(#bg)" />
      <text x="50%" y="52%" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="48" font-weight="700" fill="white">
        ${initial}
      </text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const normalizeYearLabels = (yearValue) => {
  if (Array.isArray(yearValue)) {
    return yearValue
      .map((entry) => {
        if (entry && typeof entry === "object") {
          return (
            entry.name ||
            entry.title ||
            entry.label ||
            entry.year ||
            entry.standard ||
            entry.id
          );
        }

        return entry;
      })
      .filter(Boolean)
      .map((entry) => String(entry));
  }

  if (!yearValue) return [];
  return [String(yearValue)];
};

const buildTutorCardModel = (teacher, index) => {
  const teacherName = getTeacherDisplayName(teacher);
  const teacherId = getTeacherIdentifier(teacher);
  const years = normalizeYearLabels(teacher?.year).slice(0, 4);
  const rawPricePerSession = teacher?.price_per_session ?? teacher?.price;
  const pricePerSession = normalizeAudPriceForDisplay(rawPricePerSession);
  const rating = toNumber(teacher?.rating);
  const reviewCount = toNumber(teacher?.reviews ?? teacher?.review_count ?? teacher?.total_reviews);
  const experience = toNumber(teacher?.experience);
  const description = [
    teacher?.specializations,
    teacher?.qualifications,
    teacher?.subjects,
    teacher?.bio,
  ].find((value) => String(value || "").trim());

  return {
    id: teacherId || `teacher-${index}`,
    routeId: teacherId || null,
    name: teacherName,
    image:
      teacher?.photoImage ||
      teacher?.photo ||
      teacher?.image ||
      buildAvatarDataUri(teacherName),
    rating: rating ?? "New",
    reviews:
      reviewCount !== null
        ? `${reviewCount} review${reviewCount === 1 ? "" : "s"}`
        : "Profile available",
    experience:
      experience !== null
        ? `${experience} year${experience === 1 ? "" : "s"} exp`
        : "Experience not provided",
    description:
      String(description || "").trim() ||
      "Teacher profile is available for booking.",
    years,
    price: pricePerSession,
    priceMinorUnits: normalizeAudPriceToMinorUnits(rawPricePerSession),
  };
};

const BookTutor = () => {
  const navigate = useNavigate();

  const tutorListQuery = useQuery({
    queryKey: ["book-tutors"],
    queryFn: () => teacherApi.getTutors(),
    retry: false,
  });

  const tutors = useMemo(
    () =>
      normalizeTeacherCollection(tutorListQuery.data).map((teacher, index) =>
        buildTutorCardModel(teacher, index)
      ),
    [tutorListQuery.data]
  );

  const filteredTutors = useMemo(() => {
    return tutors;
  }, [tutors]);

  const handleTutorClick = (tutorId) => {
    if (!tutorId) return;
    navigate(`/classBooking/tutorDetails/${tutorId}`);
  };

  return (
    <PageContainer>
      <div className="sticky top-0 z-50 bg-[#DFF2EB]/70 backdrop-blur-md">
        <Header />
      </div>

      <MainContent>
        <SectionTitle 
          title="Find Your Perfect Tutor" 
          subtitle="Select year level to find qualified tutors" 
        />

        <p className="mt-6 text-gray-700 font-medium">
          Showing {filteredTutors.length} tutors
        </p>

        {tutorListQuery.isLoading ? (
          <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-600 shadow-sm">
            Loading tutors...
          </div>
        ) : tutorListQuery.isError ? (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-red-700 shadow-sm">
            {tutorListQuery.error.message || "Unable to load tutors right now."}
          </div>
        ) : !filteredTutors.length ? (
          <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-600 shadow-sm">
            No tutors found for your current search.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-6 mt-6">
            {filteredTutors.map((tutor, index) => (
              <TutorCard
                key={tutor.id}
                tutor={tutor}
                onClick={() => handleTutorClick(tutor.routeId)}
              />
            ))}
          </div>
        )}
      </MainContent>
    </PageContainer>
  );
};

export default BookTutor;
